import Reflux from 'reflux';
import { Map, List, OrderedMap, fromJS } from 'immutable';
import BaseMixin from 'flux/stores/BaseMixin';
import CuesheetActions from 'flux/actions/cuesheet/CuesheetActions';
import CuesheetRowActions from 'flux/actions/cuesheet/CuesheetRowActions';
import CuesheetTrackingActions from 'flux/actions/cuesheet/CuesheetTrackingActions';
import CuesheetCueClockActions from 'flux/actions/cuesheet/CuesheetCueClockActions';
import UserActions from 'flux/actions/UserActions';
import RealTimeActions from 'flux/actions/RealTimeActions';
import RouteActions from 'flux/actions/RouteActions';
import {
  isTeleprompter,
  isDisplayView,
} from 'main/components/show/cuesheet/cuesheetViewHelper';

import UserSessionStore from 'flux/stores/UserSessionStore';
import socket from 'shared/lib/socket';

import _ from 'underscore';

/**
 * Cuesheet Tracking Store
 *
 */
export default Reflux.createStore({
  listenables: [
    CuesheetActions,
    RealTimeActions,
    CuesheetTrackingActions,
    UserActions,
  ],

  mixins: [BaseMixin],

  init() {
    this.setState(
      Map({
        trackingAvailable: false, //Only set to true for non-show callers..allows the user to track another user's position
        trackingResumeAvailable: false, //Only set to true for show callers...allows the user to resume their own tracking position
        trackingEnabled: false, //Whether or not tracking is ON...only should be true for non show callers (when trackingAvailable === true)
        trackableUsers: List(), //list of users eligible for tracking
        selectedTrackableUser: null, //the currently selected user for tracking...only valid/relevant when trackingAvailable === true
      }),
    );

    this.listenTo(UserSessionStore, 'onUserSession');
    this.listenTo(
      CuesheetTrackingActions.clearTrackableCuePersistent.completed,
      'onClearTrackableCuePersistentCompleted',
    );

    this._socket = socket.get();
    /**
     * When the socket connects, refetch data to sync back up in case stuff happened
     */
    this._socket.on('reconnect', () => {
      this._populate(
        this._getTeamId(),
        this._getEventId(),
        this._getShowId(),
        true,
      );
    });
  },

  /**
   * Triggered when .loadFromSession() is called
   */
  onLoadFromSession() {
    /**
     * Clear out the old user data when .loadFromSession() is called. This will make sure the tracking state doesn't
     * initialize until new user data is loaded (onUserSession() is called)
     */
    delete this._user;
  },

  /**
   * Handle the user session getting loaded
   */
  onUserSession(user) {
    this._user = user;

    if (this._userLoadedCallback) {
      this._userLoadedCallback();
      this._userLoadedCallback = undefined;
    }

    this._eventContextChanged();
  },

  onClearTrackableCuePersistentCompleted(user) {
    this._user = fromJS(user);
  },

  /**
   * Used to update tracking state (whether tracking enabled for the user) after the event context has changed
   */
  _eventContextChanged() {
    let user = this._user;

    let trackingState = user.get('trackingState');

    if (trackingState && trackingState.has(this._getEventId())) {
      //Check if the user has tracking state for this event, if so...set trackingEnabled = true, and set the selectedTrackableUser
      let next = this.getState()
        .set('trackingEnabled', true)
        .set('selectedTrackableUser', trackingState.get(this._getEventId()));

      this.setStateAndTrigger(next);
    }
  },

  _getUser() {
    return this._user;
  },

  _getTeamId() {
    return this._teamId;
  },

  _getEventId() {
    return this._eventId;
  },

  _getShowId() {
    return this._showId;
  },

  /**
   * Populate the store with a team/event/show
   */
  onPopulate(teamId, eventId, showId) {
    this._populate(teamId, eventId, showId);
  },

  /**
   * Helper to do the business of actually injecting the data
   *
   * @param teamId
   * @param eventId
   * @param showId
   * @param isReconnection Boolean whether we're doing this as a result of a socket reconnection
   */
  _populate(teamId, eventId, showId, isReconnection) {
    /**
     * If there is no user yet, delay population until there is one..
     */
    if (!this._getUser()) {
      this._userLoadedCallback = () => this._populate(teamId, eventId, showId);
      return;
    }

    let userIsShowCaller = _userIsShowCaller(this._getUser(), eventId),
      trackingAvailable = true; //Used to be that we only had this true if the user is NOT a show caller, see #1125

    this._teamId = teamId;
    this._eventId = eventId;
    this._showId = showId;

    this._eventContextChanged();

    let next = this.getState()
      .set('trackingAvailable', trackingAvailable)
      .set('trackingResumeAvailable', false)
      .asMutable();

    if (trackingAvailable) {
      CuesheetTrackingActions.populateShowCallers(teamId, eventId);
    }

    if (
      this._currentUserHasTrackableState() &&
      userIsShowCaller &&
      !this.getInState('trackingEnabled')
    ) {
      /**
       * 1. Set tracking resume available
       */
      next.set('trackingResumeAvailable', true);

      /**
       * 2. Set the tracking position
       */
      let trackableState = this._getCurrentUserTrackableState();
      let { showId, itemSetId, itemSetRowId } = trackableState.toJS();

      /**
       * TODO: ... I think if we skip this step when the show caller is tracking someone else, we'll fix #1360
       */
      if (this._getShowId() === showId) {
        CuesheetTrackingActions.setInitialTrackingPosition(
          showId,
          itemSetId,
          itemSetRowId,
        );
        CuesheetRowActions.trackRowTransient(showId, itemSetId, itemSetRowId);
      }

      /**
       * 3. Populate the clock, unless we're just refreshing after socket reconnection
       *
       */
      if (!isReconnection) {
        this._populateClock(this._getUser().get('_id'), trackableState);
      }
    }

    this.setStateAndTrigger(next.asImmutable());
  },

  _populateClock(userId, trackableState) {
    if (trackableState && trackableState.get('showId') === this._getShowId()) {
      CuesheetCueClockActions.populate(
        userId,
        this._getEventId(),
        this._getShowId(),
        trackableState.get('cueClock'),
      );
    } else {
      //Clear the clock, which is necessary in cases where you've switched shows, and the clock was active on the previous show
      CuesheetCueClockActions.clearClock();
    }
  },

  onCueClockMutatedRealTime(userId, showId, payload) {
    //set payload as cueClock state
    if (this.getState().hasIn(['trackableUsers', userId])) {
      this.setInState(
        [
          'trackableUsers',
          userId,
          'trackableState',
          payload.eventId,
          'cueClock',
        ],
        Map(payload),
      );
    }
  },

  onEventUpdateRoleRealTime({ teamId, eventId }) {
    CuesheetTrackingActions.populateShowCallers(teamId, eventId);
  },

  onPopulateShowCallersCompleted(users) {
    const filteredUsers = users.filter((user) => !user.pending);

    let next = this.getState().set(
      'trackableUsers',
      OrderedMap(
        filteredUsers.map((userJson) => [
          userJson._id,
          fromJS(
            _.pick(userJson, 'firstName', 'lastName', '_id', 'trackableState'),
          ),
        ]),
      ),
    );

    if (next.get('trackableUsers').size === 0) {
      //If there are no users, delete any selected trackable user...this could happen if someone tracked a show caller that was subsequently demoted
      next = next.delete('selectedTrackableUser').delete('trackingEnabled');
    }

    //TODO: what to do if the selectedTrackableUser is not in the trackableUsersList??
    //TODO: what to do if the selectedTrackableUser is not in the trackableUsersList??
    //TODO: what to do if the selectedTrackableUser is not in the trackableUsersList??

    if (
      this.getInState('trackingEnabled') &&
      next.get('selectedTrackableUser')
    ) {
      //Tracking is enabled, populate the clock
      let trackedUserId = next.get('selectedTrackableUser');

      let trackableState = next.getIn([
        'trackableUsers',
        trackedUserId,
        'trackableState',
        this._getEventId(),
      ]);

      if (
        trackableState &&
        trackableState.get('showId') === this._getShowId()
      ) {
        this._startTrackingSomeoneTransient(trackedUserId, trackableState);
      }
    }

    this.setStateAndTrigger(next);
  },

  onSelectTrackableUser(userId) {
    CuesheetRowActions.clearTrackedRowTransient();
    this.setInStateAndTrigger('selectedTrackableUser', userId);

    this._startTrackingSomeone(userId);
  },

  /**
   * Handle when a user clicks the toggle switch for tracking
   */
  onToggleTrackingEnabled() {
    //Ignore if there are no trackable users
    if (this.getInState('trackableUsers').size === 0) return;

    let willBeEnabled = this.getInState('trackingEnabled') ? false : true;

    //Set the new tracking value
    this.setInStateAndTrigger('trackingEnabled', willBeEnabled);

    //If tracking is now enabled, update the tracking user
    if (willBeEnabled) {
      CuesheetRowActions.clearTrackedRowTransient();

      let selectedUserId = this.getInState('selectedTrackableUser');

      if (selectedUserId) {
        this._startTrackingSomeone(selectedUserId);
      }
    } else {
      //Else clear the tracking user

      CuesheetTrackingActions.clearTracking(
        this._getUser().get('_id'),
        this._getEventId(),
      );
      CuesheetRowActions.clearTrackedRowTransient();
      CuesheetCueClockActions.clearClock();
    }
  },

  /**
   * Helper function used when someone toggles tracking or changes the target user to track
   */
  _startTrackingSomeone(userId) {
    const user = this._getUser();

    if (!user) return;

    //Persist the tracking state
    CuesheetTrackingActions.updateTrackingUser(
      user.get('_id'),
      this._getEventId(),
      userId,
    );

    this._startTrackingSomeoneTransient(userId);
  },

  /**
   * Helper function to set transient state for tracking (the tracking bar, and the clock)
   */
  _startTrackingSomeoneTransient(userId, trackableState) {
    trackableState =
      trackableState ||
      this.getInState([
        'trackableUsers',
        userId,
        'trackableState',
        this._getEventId(),
      ]);

    //Start tracking at the trackable user's position
    if (trackableState) {
      this._populateClock(userId, trackableState);

      let { showId, itemSetId, itemSetRowId } = trackableState.toJS();

      this._jumpToTrackingPosition(showId, itemSetId, itemSetRowId);
    }
  },

  /**
   * RealTime handler for tracking messages
   */
  onItemRowTrackedRealTime(userId, showId, payload, responsePayload) {
    let { eventId, itemSetId, itemSetRowId, trackedOn } = payload;
    let user = responsePayload;

    /**
     * If we have received a 'newer' one than the incoming message, do nothing.
     */
    if (this._lastTrackedOn && trackedOn < this._lastTrackedOn) return;

    this._lastTrackedOn = trackedOn;

    //Update the trackable user state, so that IF the user does track this user later,
    //it will have the right position in state to start tracking
    let next = this.getState().mergeIn(
      ['trackableUsers', userId],
      fromJS(_.pick(user, 'firstName', 'lastName', '_id')),
    );

    this.setStateAndTrigger(
      next.mergeIn(
        ['trackableUsers', userId, 'trackableState', eventId],
        Map({ showId, itemSetId, itemSetRowId }),
      ),
    );

    /**
     * If they aren't tracking...keep moving!
     */
    if (!this.getInState('trackingEnabled')) return;

    let trackedUserId = this.getInState('selectedTrackableUser');

    //If the eventId isn't the same as the one we're in, or the user isn't the same as the one we're tracking, keep moving!
    if (this._getEventId() !== eventId || trackedUserId !== userId) return;

    this._populateClock(userId, this._getSelectedUserTrackableState());

    this._jumpToTrackingPosition(showId, itemSetId, itemSetRowId, user);
  },

  //TODO: add clock state change hook

  /**
   * Resume the tracking position a user has set for their self
   */
  onResumeTrackingPosition() {
    const trackableState = this._currentUserHasTrackableState()
      ? this._getCurrentUserTrackableState()
      : this._getSelectedUserTrackableState();

    const { showId, itemSetId, itemSetRowId } = trackableState.toJS();

    this._jumpToTrackingPosition(showId, itemSetId, itemSetRowId);
  },

  onUpdateTrackableCue(userId, eventId, showId, itemSetId, itemSetRowId) {
    let next = this.getState();

    /**
     * 1. If tracking resume wasn't available (because there was no trackable state) it should now be available
     */
    if (!next.get('trackingResumeAvailable')) {
      next = next.set('trackingResumeAvailable', true);
    }

    next = next.mergeIn(
      ['trackableUsers', userId, 'trackableState', eventId],
      Map({ showId, itemSetId, itemSetRowId }),
    );
    this.setStateAndTrigger(next);

    /**
     * 2. Set initial tracking position
     */
    CuesheetTrackingActions.setInitialTrackingPosition(
      showId,
      itemSetId,
      itemSetRowId,
    );

    /**
     * 3. Update trackable state, so that when they tracking is resumed, we can go where we last tracked
     */
    this._user = this._getUser().setIn(
      ['trackableState', eventId],
      Map({ itemSetId, showId, itemSetRowId }),
    );
  },

  onUserRemovedTrackingRealTime(params, payload) {
    let userIdWhoStoppedBroadcasting = params.userId;

    /**
     * Clear the trackable cue transient state, if the user is tracking the remote user that stopped tracking
     */
    this._clearTrackableCueTransient(
      userIdWhoStoppedBroadcasting,
      payload.eventId,
    );

    /**
     * NOTE:
     * We do not make an explicit call here to clear the clock, as it is is handled by a separate realtime hook in the CuesheetCueClockStore
     */
  },

  onClearTrackableCue() {
    const userId = this._getUser().get('_id');

    /**
     * Clear trackable cue transient state
     */
    this._clearTrackableCueTransient(userId, this._getEventId());

    /**
     * Update the API/database (clear the tracking state for the user)
     */
    CuesheetTrackingActions.clearTrackableCuePersistent(
      userId,
      this._getEventId(),
      this._getShowId(),
    );

    /**
     * Zero out the clock
     */
    CuesheetCueClockActions.clearClock();
  },

  /**
   * Clear tracked row transient state
   */
  _clearTrackableCueTransient(userId, eventId, next) {
    next = this.getState() || next;
    /**
     * Clear the actual tracking bar
     */
    CuesheetRowActions.clearTrackedRowTransient(userId);
    next = next.set('trackingResumeAvailable', false);
    next = next.deleteIn(['trackableUsers', userId, 'trackableState', eventId]);

    this.setStateAndTrigger(next);
  },

  /**
   * Get trackable state for the current user+event
   */
  _getCurrentUserTrackableState() {
    return this._getUser().getIn(['trackableState', this._getEventId()]);
  },

  /**
   * Get trackable state for tracked user
   */
  _getSelectedUserTrackableState() {
    let next = this.getState();
    let trackedUserId = next.get('selectedTrackableUser');
    return next.getIn([
      'trackableUsers',
      trackedUserId,
      'trackableState',
      this._getEventId(),
    ]);
  },

  /**
   * Check if the current user has trackable state for the current event
   */
  _currentUserHasTrackableState() {
    return this._getUser().hasIn(['trackableState', this._getEventId()]);
  },

  /**
   * Jump the user to a tracking position
   *
   * 1. If the user is not in the show, switch them to the show and start tracking at the right place in that show
   * 2. If the user IS in the show, track the row transient
   *
   */
  _jumpToTrackingPosition(showId, itemSetId, itemSetRowId, user) {
    /**
     * We always se the initial tracking position, because if the user loses socket connection, we have to refresh,
     * and we need to retain the tracking position they were at
     */
    CuesheetTrackingActions.setInitialTrackingPosition(
      showId,
      itemSetId,
      itemSetRowId,
      user,
    );

    /**
     * If the incoming tracked row is in another show, route to that show, else track the row transient
     */
    if (this._getShowId() !== showId) {
      if (isDisplayView() || isTeleprompter()) return;
      RouteActions.goToShowByTracking({
        teamId: this._getTeamId(),
        eventId: this._getEventId(),
        showId,
      });
    } else {
      CuesheetRowActions.trackRowTransient(showId, itemSetId, itemSetRowId);
    }
  },
});

function _userIsShowCaller(user, eventId) {
  let found = user
    .get('events')
    .find(
      (eventRole) =>
        eventRole.get('event') === eventId &&
        eventRole.get('role') === 'showCaller',
    );

  return found ? true : false;
}
